import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import Logo from "@/components/Logo";
import { AuthAside } from "@/components/AuthAside";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Loader2, ArrowRight, ArrowLeft, MailCheck, Lock } from "lucide-react";

const LEVELS = [
  { value: "B1", label: "B1", note: "CLB 5" },
  { value: "B2", label: "B2", note: "CLB 7" },
];

export default function Register() {
  const { user, startRegister, verifyRegister, resendCode } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", target_level: "B1", referred_by: "" });
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user && user !== false) navigate("/dashboard"); }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitDetails = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await startRegister(form);
    setLoading(false);
    if (res.ok) { setDevCode(res.data?.dev_code || ""); setStep(2); }
    else setError(res.error);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await verifyRegister(form.email, code);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  const resend = async () => {
    const res = await resendCode(form.email);
    if (res.ok) setDevCode(res.data?.dev_code || "");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="register-page">
      <AuthAside titleKey="auth.dreamTitle" subKey="auth.dreamSub" showEncrypted />

      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute top-5 right-5"><LanguageToggle /></div>
        <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size={38} textClass="text-2xl" /></div>

          {step === 1 ? (
            <>
              <h1 className="font-display text-3xl font-semibold mb-1">{t("auth.regTitle")}</h1>
              <p className="text-muted-foreground mb-6 text-sm">{t("auth.regStep1")}</p>
              <form onSubmit={submitDetails} className="space-y-4" data-testid="register-form">
                <div>
                  <label className="text-sm font-medium">{t("auth.fullName")}</label>
                  <input data-testid="reg-name-input" value={form.name} onChange={set("name")} required
                    className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="John Doe" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">{t("auth.email")}</label>
                    <input data-testid="reg-email-input" type="email" value={form.email} onChange={set("email")} required
                      className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t("auth.whatsapp")}</label>
                    <input data-testid="reg-phone-input" value={form.phone} onChange={set("phone")} required
                      className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="+91 98765 43210" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("auth.password")}</label>
                  <input data-testid="reg-password-input" type="password" value={form.password} onChange={set("password")} required minLength={6}
                    className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="At least 6 characters" />
                </div>
                <div>
                  <label className="text-sm font-medium">{t("auth.targetLevel")}</label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {LEVELS.map((l) => (
                      <button type="button" key={l.value} onClick={() => setForm((f) => ({ ...f, target_level: l.value }))} data-testid={`reg-level-${l.value}`}
                        className={`rounded-xl py-3 text-center transition-colors ${form.target_level === l.value ? "emerald-surface text-white" : "bg-secondary hover:bg-accent"}`}>
                        <div className="font-display font-semibold text-lg">{l.label}</div>
                        <div className="text-[11px] opacity-80">{l.note}</div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {["C1", "C2"].map((l) => (
                      <div key={l} data-testid={`reg-level-${l}-soon`} className="rounded-xl py-2 text-center bg-secondary/50 text-muted-foreground cursor-not-allowed">
                        <div className="inline-flex items-center gap-1 text-sm font-semibold"><Lock className="h-3 w-3" /> {l}</div>
                        <div className="text-[10px] opacity-70">Coming soon</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("auth.referredBy")} <span className="text-muted-foreground font-normal">{t("auth.optional")}</span></label>
                  <input data-testid="reg-referred-input" value={form.referred_by} onChange={set("referred_by")}
                    className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Instagram, a friend, YouTube…" />
                </div>
                {error && <div data-testid="register-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={loading} data-testid="reg-continue-btn"
                  className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("common.continue")} <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
              <p className="mt-6 text-sm text-muted-foreground text-center">
                {t("auth.alreadyHave")} <Link to="/login" data-testid="switch-to-login" className="text-primary font-semibold hover:underline">{t("auth.login")}</Link>
              </p>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> {t("common.back")}</button>
              <div className="h-12 w-12 rounded-2xl emerald-surface grid place-items-center mb-4"><MailCheck className="h-6 w-6 text-white" /></div>
              <h1 className="font-display text-3xl font-semibold mb-1">{t("auth.verifyTitle")}</h1>
              <p className="text-muted-foreground mb-6 text-sm">{t("auth.verifySub")} <span className="font-semibold text-foreground">{form.email}</span>.</p>

              {devCode && (
                <div className="mb-4 rounded-xl bg-accent text-accent-foreground text-sm px-4 py-3" data-testid="dev-code-box">
                  <span className="font-semibold">Test mode:</span> your code is <span className="font-mono font-bold tracking-widest">{devCode}</span>
                  <span className="block text-xs opacity-80 mt-0.5">(Emails run in console mode until an email key is added.)</span>
                </div>
              )}

              <form onSubmit={submitCode} className="space-y-4" data-testid="verify-form">
                <input data-testid="verify-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required
                  className="w-full h-14 px-4 rounded-xl bg-card border border-border text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-ring" placeholder="000000" />
                {error && <div data-testid="verify-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={loading || code.length !== 6} data-testid="verify-submit-btn"
                  className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("auth.verifyContinue")} <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
              <button onClick={resend} data-testid="resend-code-btn" className="mt-4 text-sm text-primary font-semibold hover:underline">{t("auth.resend")}</button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
